import { FC, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import TopBarProgress from 'react-topbar-progress-indicator';

import { getCSSVariableValue } from '@/_metronic/assets/ts/_utils';
import { WithChildren } from '@/_metronic/helpers';
import { AccessDenied } from '@/_metronic/layout/components/AccessDenied';
import { PageTitle } from '@/_metronic/layout/core';
import { MasterLayout } from '@/_metronic/layout/MasterLayout';
import ApplicationPage from '@/modules/application/ApplicationPage';
import CommunicationPage from '@/modules/communication/CommunicationPage';
import CustomerPage from '@/modules/customer/CustomerPage';
import FactoryPage from '@/modules/factory/FactoryPage';
import { Profile } from '@/modules/hr-management/components/Profile';
import HRPage from '@/modules/hr-management/HRPage';
import JobPostPage from '@/modules/job-post/JobPostPage';
import LeaveRequestPage from '@/modules/leave-request/LeaveRequestPage';
import NotificationPage from '@/modules/notification/NotificationPage';
import OperateWorker from '@/modules/operate-worker/OperateWorker';
import PayrollPage from '@/modules/payroll/PayrollPage';
import RecruitmentOrganizationPage from '@/modules/recruitment-organization/RecruitmentOrganizationPage';
import RecruitmentPage from '@/modules/recruitment-plan/RecruitmentPage';
import ReportPage from '@/modules/report/ReportPage';
import SystemParameterPage from '@/modules/system-parameter/SystemParameterPage';
import DashboardPage from '@/modules/dashboard/DashboardPage';
import ViolationPage from '@/modules/violation/ViolationPage';
import WorkerPage from '@/modules/worker/WorkerPage';

const PrivateRoutes = () => {
  return (
    <Routes>
      <Route element={<MasterLayout />}>
        <Route element={<Navigate to="/recruitment-plan/list" />} path="auth/*" />
        {/* Pages */}
        <Route
          element={
            <SuspensedView>
              <DashboardPage />
            </SuspensedView>
          }
          path="dashboard/*"
        />
        {/* profile */}
        <Route
          element={
            <SuspensedView>
              <PageTitle
                breadcrumbs={[
                  {
                    isActive: false,
                    isSeparator: false,
                    path: '/profile',
                    title: 'Thông tin cá nhân',
                  },
                ]}
              />
              <Profile />
            </SuspensedView>
          }
          path="profile/"
        />
        {/* recruitment */}
        <Route
          element={
            <SuspensedView>
              <RecruitmentPage />
            </SuspensedView>
          }
          path="recruitment-plan/*"
        />
        {/* recruitment-organization */}
        <Route
          element={
            <SuspensedView>
              <RecruitmentOrganizationPage />
            </SuspensedView>
          }
          path="recruitment-organization/*"
        />
        {/* customer-management */}
        <Route
          element={
            <SuspensedView>
              <CustomerPage />
            </SuspensedView>
          }
          path="customer/*"
        />
        {/* hr-management */}
        <Route
          element={
            <SuspensedView>
              <HRPage />
            </SuspensedView>
          }
          path="hr-management/*"
        />

        {/* labor-operation */}
        <Route
          element={
            <SuspensedView>
              <OperateWorker />
            </SuspensedView>
          }
          path="operate-worker/*"
        />

        {/* labor-operation */}
        <Route
          element={
            <SuspensedView>
              <CommunicationPage />
            </SuspensedView>
          }
          path="communication/*"
        />

        {/* system-parameter — T-CFG-02. Menu link uses /system-parameter
            (set in useMenu.ts). Old /account-management path kept as an
            alias so any bookmarked URL still resolves. */}
        <Route
          element={
            <SuspensedView>
              <SystemParameterPage />
            </SuspensedView>
          }
          path="system-parameter/*"
        />
        <Route
          element={
            <SuspensedView>
              <SystemParameterPage />
            </SuspensedView>
          }
          path="account-management/*"
        />
        {/* factory (V1.4 A1) */}
        <Route
          element={
            <SuspensedView>
              <FactoryPage />
            </SuspensedView>
          }
          path="factory/*"
        />
        {/* report (Phần IV — 17 báo cáo) */}
        <Route
          element={
            <SuspensedView>
              <ReportPage />
            </SuspensedView>
          }
          path="report/*"
        />
        {/* T-TT-02 notification */}
        <Route
          element={<SuspensedView><NotificationPage /></SuspensedView>}
          path="notification/*"
        />
        {/* T-TT-01 job post */}
        <Route
          element={<SuspensedView><JobPostPage /></SuspensedView>}
          path="job-post/*"
        />
        {/* T-TT-03 application */}
        <Route
          element={<SuspensedView><ApplicationPage /></SuspensedView>}
          path="application/*"
        />
        {/* T-VH-05 violation */}
        <Route
          element={<SuspensedView><ViolationPage /></SuspensedView>}
          path="violation/*"
        />
        {/* T-VH-06 leave request */}
        <Route
          element={<SuspensedView><LeaveRequestPage /></SuspensedView>}
          path="leave-request/*"
        />
        {/* T-TCKT payroll + reconcile + settlement */}
        <Route
          element={<SuspensedView><PayrollPage /></SuspensedView>}
          path="payroll/*"
        />
        {/* worker */}
        <Route
          element={
            <SuspensedView>
              <WorkerPage />
            </SuspensedView>
          }
          path="worker/*"
        />
        {/* worker */}
        <Route element={<AccessDenied />} path="/access-denied" />
        {/* Page Not Found */}
        <Route element={<Navigate to="/error/404" />} path="*" />
      </Route>
    </Routes>
  );
};

const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue('--kt-primary');
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  });

  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>;
};

export { PrivateRoutes };
